import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiJSON, apiFetch } from '../config/api';
import notificationService from '../utils/notificationService';
import { fetchBooks } from '../queries/books';

// État initial
const initialState = {
  books: [],
  conversations: [],
  currentBook: null,
  currentConversation: null,
  isLoading: false,
  error: null,
  /** @type {{ bookId: string, chunkIdx: number, charStart: number|null, charEnd: number|null, requestId: number } | null} */
  streamHighlight: null,
  /** Préremplissage barre question (ex: clic graphe → IA) */
  chatDraftQuery: '',
  /** Tutoriel react-joyride : uniquement déclenché manuellement (run=true) */
  onboardingTourRun: false,
  userPreferences: {
    theme: 'light',
    language: 'fr',
    autoSave: true
  }
};

// Types d'actions
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_BOOKS: 'SET_BOOKS',
  SET_CONVERSATIONS: 'SET_CONVERSATIONS',
  SET_CURRENT_BOOK: 'SET_CURRENT_BOOK',
  SET_CURRENT_CONVERSATION: 'SET_CURRENT_CONVERSATION',
  ADD_BOOK: 'ADD_BOOK',
  UPDATE_BOOK: 'UPDATE_BOOK',
  DELETE_BOOK: 'DELETE_BOOK',
  ADD_CONVERSATION: 'ADD_CONVERSATION',
  UPDATE_CONVERSATION: 'UPDATE_CONVERSATION',
  DELETE_CONVERSATION: 'DELETE_CONVERSATION',
  UPDATE_PREFERENCES: 'UPDATE_PREFERENCES',
  SET_STREAM_HIGHLIGHT: 'SET_STREAM_HIGHLIGHT',
  CLEAR_STREAM_HIGHLIGHT: 'CLEAR_STREAM_HIGHLIGHT',
  SET_CHAT_DRAFT_QUERY: 'SET_CHAT_DRAFT_QUERY',
  SET_ONBOARDING_TOUR_RUN: 'SET_ONBOARDING_TOUR_RUN'
};

// Reducer
function appReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
    
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };
    
    case ACTIONS.SET_BOOKS:
      return { ...state, books: action.payload, isLoading: false };
    
    case ACTIONS.SET_CONVERSATIONS:
      return { ...state, conversations: action.payload, isLoading: false };
    
    case ACTIONS.SET_CURRENT_BOOK:
      return { ...state, currentBook: action.payload };
    
    case ACTIONS.SET_CURRENT_CONVERSATION:
      return { ...state, currentConversation: action.payload };
    
    case ACTIONS.ADD_BOOK:
      return { ...state, books: [...state.books, action.payload] };
    
    case ACTIONS.UPDATE_BOOK:
      return {
        ...state,
        books: state.books.map(book => 
          book.id === action.payload.id ? action.payload : book
        )
      };
    
    case ACTIONS.DELETE_BOOK:
      return {
        ...state,
        books: state.books.filter(book => book.id !== action.payload)
      };
    
    case ACTIONS.ADD_CONVERSATION:
      return { ...state, conversations: [...state.conversations, action.payload] };
    
    case ACTIONS.UPDATE_CONVERSATION:
      return {
        ...state,
        conversations: state.conversations.map(conv => 
          conv.id === action.payload.id ? action.payload : conv
        )
      };
    
    case ACTIONS.DELETE_CONVERSATION:
      return {
        ...state,
        conversations: state.conversations.filter(conv => conv.id !== action.payload)
      };
    
    case ACTIONS.UPDATE_PREFERENCES:
      return {
        ...state,
        userPreferences: { ...state.userPreferences, ...action.payload }
      };

    case ACTIONS.SET_STREAM_HIGHLIGHT:
      return { ...state, streamHighlight: action.payload };

    case ACTIONS.CLEAR_STREAM_HIGHLIGHT:
      return { ...state, streamHighlight: null };

    case ACTIONS.SET_CHAT_DRAFT_QUERY:
      return { ...state, chatDraftQuery: action.payload ?? '' };

    case ACTIONS.SET_ONBOARDING_TOUR_RUN:
      return { ...state, onboardingTourRun: Boolean(action.payload) };
    
    default:
      return state;
  }
}

// Création du contexte
const AppContext = createContext();

// Hook personnalisé pour utiliser le contexte
export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}

// Provider principal
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const queryClient = useQueryClient();
  const booksLoadErrorShownRef = useRef(false);

  const booksQuery = useQuery({
    queryKey: ['books'],
    queryFn: fetchBooks,
  });

  useEffect(() => {
    // TanStack Query v4 : isLoading (v5 : isPending)
    if (booksQuery.isLoading) {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    }
  }, [booksQuery.isLoading]);

  useEffect(() => {
    if (booksQuery.data !== undefined) {
      dispatch({ type: ACTIONS.SET_BOOKS, payload: booksQuery.data });
      booksLoadErrorShownRef.current = false;
    }
  }, [booksQuery.data]);

  useEffect(() => {
    if (booksQuery.isError && booksQuery.error) {
      if (!booksLoadErrorShownRef.current) {
        booksLoadErrorShownRef.current = true;
        notificationService.handleApiError(booksQuery.error, 'Erreur lors du chargement des livres');
        dispatch({ type: ACTIONS.SET_ERROR, payload: booksQuery.error.message });
      }
    }
  }, [booksQuery.isError, booksQuery.error]);

  const loadBooks = useCallback(() => {
    booksLoadErrorShownRef.current = false;
    queryClient.invalidateQueries({ queryKey: ['books'] });
  }, [queryClient]);

  const addBook = async (bookData) => {
    try {
      const newBook = await apiJSON('books', {
        method: 'POST',
        body: JSON.stringify(bookData)
      });
      await queryClient.invalidateQueries({ queryKey: ['books'] });
      notificationService.success('Livre ajouté avec succès');
      return newBook;
    } catch (error) {
      notificationService.handleApiError(error, 'Erreur lors de l\'ajout du livre');
      throw error;
    }
  };

  const updateBook = async (bookId, updates) => {
    try {
      const updatedBook = await apiJSON(`books/${bookId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      await queryClient.invalidateQueries({ queryKey: ['books'] });
      notificationService.success('Livre mis à jour avec succès');
      return updatedBook;
    } catch (error) {
      notificationService.handleApiError(error, 'Erreur lors de la mise à jour du livre');
      throw error;
    }
  };

  const deleteBook = async (bookId) => {
    try {
      await apiJSON(`books/${bookId}`, { method: 'DELETE' });
      await queryClient.invalidateQueries({ queryKey: ['books'] });
      notificationService.success('Livre supprimé avec succès');
    } catch (error) {
      notificationService.handleApiError(error, 'Erreur lors de la suppression du livre');
      throw error;
    }
  };

  // Actions pour les conversations (liste globale ; le détail / streaming vit surtout dans IA_Persistent.jsx)
  const loadConversations = useCallback(async () => {
    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      const data = await apiJSON('conversations/list');
      dispatch({ type: ACTIONS.SET_CONVERSATIONS, payload: data.conversations || [] });
    } catch (error) {
      notificationService.handleApiError(error, 'Erreur lors du chargement des conversations');
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  const createConversation = async (title) => {
    try {
      const formData = new FormData();
      formData.append('title', title || 'Nouvelle conversation');

      const response = await apiFetch('conversations/create', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const newConversation = await response.json();
        dispatch({ type: ACTIONS.ADD_CONVERSATION, payload: newConversation });
        notificationService.success('Conversation créée avec succès');
        return newConversation;
      } else {
        throw new Error('Erreur lors de la création de la conversation');
      }
    } catch (error) {
      notificationService.handleApiError(error, 'Erreur lors de la création de la conversation');
      throw error;
    }
  };

  const updateConversation = async (conversationId, updates) => {
    try {
      const updatedConversation = await apiJSON(`/conversations/${conversationId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      dispatch({ type: ACTIONS.UPDATE_CONVERSATION, payload: updatedConversation });
      notificationService.success('Conversation mise à jour avec succès');
      return updatedConversation;
    } catch (error) {
      notificationService.handleApiError(error, 'Erreur lors de la mise à jour de la conversation');
      throw error;
    }
  };

  const deleteConversation = async (conversationId) => {
    try {
      await apiJSON(`/conversations/${conversationId}`, { method: 'DELETE' });
      dispatch({ type: ACTIONS.DELETE_CONVERSATION, payload: conversationId });
      notificationService.success('Conversation supprimée avec succès');
    } catch (error) {
      notificationService.handleApiError(error, 'Erreur lors de la suppression de la conversation');
      throw error;
    }
  };

  // Actions générales
  const setCurrentBook = (book) => {
    dispatch({ type: ACTIONS.SET_CURRENT_BOOK, payload: book });
  };

  const setCurrentConversation = (conversation) => {
    dispatch({ type: ACTIONS.SET_CURRENT_CONVERSATION, payload: conversation });
  };

  const setStreamHighlight = useCallback((payload) => {
    dispatch({
      type: ACTIONS.SET_STREAM_HIGHLIGHT,
      payload: payload && typeof payload.requestId !== 'number'
        ? { ...payload, requestId: Date.now() }
        : payload,
    });
  }, []);

  const clearStreamHighlight = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_STREAM_HIGHLIGHT });
  }, []);

  const setChatDraftQuery = useCallback((text) => {
    dispatch({ type: ACTIONS.SET_CHAT_DRAFT_QUERY, payload: text ?? '' });
  }, []);

  const setOnboardingTourRun = useCallback((run) => {
    dispatch({ type: ACTIONS.SET_ONBOARDING_TOUR_RUN, payload: run });
  }, []);

  const updatePreferences = (preferences) => {
    dispatch({ type: ACTIONS.UPDATE_PREFERENCES, payload: preferences });
    // Sauvegarder dans localStorage
    localStorage.setItem('userPreferences', JSON.stringify({
      ...state.userPreferences,
      ...preferences
    }));
  };

  // Charger les préférences au démarrage
  useEffect(() => {
    const savedPreferences = localStorage.getItem('userPreferences');
    if (savedPreferences) {
      const preferences = JSON.parse(savedPreferences);
      dispatch({ type: ACTIONS.UPDATE_PREFERENCES, payload: preferences });
    }
  }, []);

  // Thème global (design-system.css, composants [data-theme='dark'])
  useEffect(() => {
    const theme = state.userPreferences?.theme === 'dark' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
  }, [state.userPreferences?.theme]);

  const value = {
    ...state,
    // Actions des livres
    loadBooks,
    addBook,
    updateBook,
    deleteBook,
    setCurrentBook,
    // Actions des conversations
    loadConversations,
    createConversation,
    updateConversation,
    deleteConversation,
    setCurrentConversation,
    setStreamHighlight,
    clearStreamHighlight,
    setChatDraftQuery,
    setOnboardingTourRun,
    // Actions générales
    updatePreferences
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}
